/**
 * G8 — map personal favorite contacts → nearby SP groups (☆ universe).
 * Not discovery-page intersection — empty only when there are no favorites.
 */

import type { PendingFavoriteSpContact } from '../account/pendingFavoriteSpContacts.js';
import type { SalesPointContactItemDTO } from '../account/salesPointContactsTypes.js';
import type { DiscoveryIntentSalesPointGroup } from './groupDiscoveryOffersBySalesPoint.js';

export type FavoriteContactLike = Pick<
  SalesPointContactItemDTO,
  | 'salesPointId'
  | 'tenantId'
  | 'tenantCode'
  | 'tenantName'
  | 'code'
  | 'name'
  | 'customerShopUrl'
> & {
  readonly isFavorite?: boolean;
};

function toGroup(contact: FavoriteContactLike): DiscoveryIntentSalesPointGroup | null {
  if (typeof contact.salesPointId !== 'number' || contact.salesPointId <= 0) {
    return null;
  }
  const tenantCode = contact.tenantCode.trim();
  if (tenantCode.length === 0) {
    return null;
  }
  const code = contact.code.trim().length > 0 ? contact.code.trim() : String(contact.salesPointId);
  const name = contact.name.trim().length > 0 ? contact.name.trim() : code;
  return {
    salesPointId: contact.salesPointId,
    tenantId: contact.tenantId,
    tenantCode,
    tenantName: contact.tenantName.trim().length > 0 ? contact.tenantName.trim() : tenantCode,
    salesPointCode: code,
    salesPointName: name,
    customerShopUrl: contact.customerShopUrl,
    distanceMeters: null,
    bestRelevanceScore: 0,
    offers: [],
  };
}

/** Signed-in L3 contacts → ☆ list groups (favorites only when isFavorite is set). */
export function mapSignedInFavoriteContactsToSalesPointGroups(
  items: readonly SalesPointContactItemDTO[],
): DiscoveryIntentSalesPointGroup[] {
  const groups: DiscoveryIntentSalesPointGroup[] = [];
  for (const item of items) {
    if (item.isFavorite === false) {
      continue;
    }
    const group = toGroup(item);
    if (group !== null) {
      groups.push(group);
    }
  }
  return groups;
}

/** Guest pending favorites → ☆ list groups. */
export function mapPendingFavoriteContactsToSalesPointGroups(
  items: readonly PendingFavoriteSpContact[],
): DiscoveryIntentSalesPointGroup[] {
  const groups: DiscoveryIntentSalesPointGroup[] = [];
  for (const item of items) {
    if (typeof item.salesPointId !== 'number' || typeof item.tenantId !== 'number') {
      continue;
    }
    const group = toGroup({
      salesPointId: item.salesPointId,
      tenantId: item.tenantId,
      tenantCode: item.tenantCode,
      tenantName: item.tenantName,
      code: item.code,
      name: item.name,
      customerShopUrl: item.customerShopUrl,
    });
    if (group !== null) {
      groups.push(group);
    }
  }
  return groups;
}
